import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  PeopleOutlined,
  StarOutline,
  CategoryOutlined,
  QuestionAnswerOutlined,
  TrendingUp,
  TrendingDown,
  BusinessOutlined,
  ContactMailOutlined,
} from '@mui/icons-material';
import './home.css';

export default function Home() {
  const server_url = process.env.REACT_APP_API_URI;
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalRatings: 0,
    totalCategories: 0,
    totalQuestions: 0,
    totalBusinesses: 0,
    totalContactMessages: 0,
    recentUsers: [],
    recentRatings: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (
      window.location.pathname === '/admin' &&
      !window.location.search.includes('reload=true')
    ) {
      window.location.search = 'reload=true';
    }
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        let totalUsers = 0;
        let recentUsers = [];
        let totalCategories = 0;
        let totalQuestions = 0;
        let totalRatings = 0;
        let totalBusinesses = 0;
        let totalContactMessages = 0;

        // Fetch users count
        try {
          const usersRes = await axios.get(
            `${server_url}/user/get-all-user?page=1&limit=5`,
          );
          totalUsers = usersRes?.data?.totalUsers || 0;
          recentUsers = usersRes?.data?.data || [];
        } catch (err) {
          console.error('Error fetching users:', err);
        }

        // Fetch categories
        try {
          const categoriesRes = await axios.get(
            `${server_url}/context/get-all-category`,
          );
          totalCategories = categoriesRes?.data?.data?.length || 0;

          // Fetch ratings count for each category
          const categories = categoriesRes?.data?.data || [];
          for (const cat of categories) {
            try {
              const ratingsRes = await axios.get(
                `${server_url}/fetchRatingsByCatId/${cat._id}?page=1&limit=1`,
              );
              totalRatings += ratingsRes?.data?.totalRatings || 0;
            } catch (err) {
              console.error(
                `Error fetching ratings for category ${cat._id}:`,
                err,
              );
            }
          }

          // Fetch questions count for each category
          for (const cat of categories) {
            try {
              const questionsRes = await axios.post(
                `${server_url}/context/get-all-rating-questions/?page=1&limit=1`,
                { catId: cat._id },
              );
              totalQuestions += questionsRes?.data?.totalQuestions || 0;
            } catch (err) {
              console.error(
                `Error fetching questions for category ${cat._id}:`,
                err,
              );
            }
          }
        } catch (err) {
          console.error('Error fetching categories:', err);
        }

        // Fetch businesses count
        try {
          const businessRes = await axios.get(`${server_url}/business/get-all`);
          totalBusinesses = businessRes?.data?.businesses?.length || 0;
        } catch (err) {
          console.error('Error fetching businesses:', err);
        }

        // Fetch contact messages count
        try {
          const contactRes = await axios.get(`${server_url}/contactUs/all`);
          totalContactMessages = contactRes?.data?.data?.length || 0;
        } catch (err) {
          console.error('Error fetching contact messages:', err);
        }

        setStats({
          totalUsers,
          totalRatings,
          totalCategories,
          totalQuestions,
          totalBusinesses,
          totalContactMessages,
          recentUsers: recentUsers.slice(0, 5),
          recentRatings: [],
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [server_url]);

  const StatCard = ({
    icon,
    title,
    value,
    trend,
    trendValue,
    bgColor,
    iconColor,
  }) => (
    <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-gray-500 text-sm font-medium uppercase">{title}</p>
          <h3 className="text-3xl font-bold text-gray-800 mt-2">
            {loading ? '...' : value.toLocaleString()}
          </h3>
          {trend && (
            <div className="flex items-center mt-2">
              {trend === 'up' ? (
                <TrendingUp className="text-green-500 text-sm mr-1" />
              ) : (
                <TrendingDown className="text-red-500 text-sm mr-1" />
              )}
              <span
                className={`text-sm ${trend === 'up' ? 'text-green-500' : 'text-red-500'}`}
              >
                {trendValue}
              </span>
            </div>
          )}
        </div>
        <div className={`${bgColor} p-4 rounded-full`}>
          {React.cloneElement(icon, { className: `${iconColor} text-3xl` })}
        </div>
      </div>
    </div>
  );

  const roles = {
    1: 'Admin',
    2: 'Student',
    3: 'Faculty',
    4: 'Guest User',
    5: 'Business',
  };

  return (
    <div className="home min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Dashboard Overview
          </h1>
          <p className="text-gray-600">
            Welcome back! Here's what's happening with your platform.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={<PeopleOutlined />}
            title="Total Users"
            value={stats.totalUsers}
            trend="up"
            trendValue="+12%"
            bgColor="bg-blue-100"
            iconColor="text-blue-600"
          />
          <StatCard
            icon={<StarOutline />}
            title="Total Ratings"
            value={stats.totalRatings}
            trend="up"
            trendValue="+8%"
            bgColor="bg-yellow-100"
            iconColor="text-yellow-600"
          />
          <StatCard
            icon={<CategoryOutlined />}
            title="Categories"
            value={stats.totalCategories}
            bgColor="bg-purple-100"
            iconColor="text-purple-600"
          />
          <StatCard
            icon={<QuestionAnswerOutlined />}
            title="Questions"
            value={stats.totalQuestions}
            bgColor="bg-green-100"
            iconColor="text-green-600"
          />
        </div>

        {/* Second Row Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 mb-8">
          <StatCard
            icon={<BusinessOutlined />}
            title="Businesses"
            value={stats.totalBusinesses}
            bgColor="bg-indigo-100"
            iconColor="text-indigo-600"
          />
          <StatCard
            icon={<ContactMailOutlined />}
            title="Contact Messages"
            value={stats.totalContactMessages}
            bgColor="bg-pink-100"
            iconColor="text-pink-600"
          />
        </div>

        {/* Recent Activity Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Users */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Recent Users
            </h2>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : stats.recentUsers.length > 0 ? (
              <div className="space-y-3">
                {stats.recentUsers.map((user, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                        {user.username?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">
                          {user.email || 'Unknown'}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs bg-blue-100 text-blue-600 px-3 py-1 rounded-full font-medium">
                      {roles[user.role] || 'User'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No recent users
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Quick Actions
            </h2>
            <div className="grid grid-cols-1 gap-3">
              <a
                href="/admin/users"
                className="flex items-center p-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-sm hover:shadow-md"
              >
                <PeopleOutlined className="mr-3" />
                <span className="font-semibold">Manage Users</span>
              </a>
              <a
                href="/admin/category"
                className="flex items-center p-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all shadow-sm hover:shadow-md"
              >
                <CategoryOutlined className="mr-3" />
                <span className="font-semibold">Manage Categories</span>
              </a>
              <a
                href="/admin/questions"
                className="flex items-center p-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all shadow-sm hover:shadow-md"
              >
                <QuestionAnswerOutlined className="mr-3" />
                <span className="font-semibold">Manage Questions</span>
              </a>
              <a
                href="/admin/ratings"
                className="flex items-center p-4 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-lg hover:from-yellow-600 hover:to-yellow-700 transition-all shadow-sm hover:shadow-md"
              >
                <StarOutline className="mr-3" />
                <span className="font-semibold">View Ratings</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
